<script setup lang="ts">
import { computed, defineProps, ref } from "vue";
import { Hidden } from "@/types/calculators";
import { ownerOrder } from "@/constants/bazi";
import { useI18n } from "vue-i18n";
import ModalComponent from "@/components/ModalComponent.vue";

const props = defineProps<{ hidden: Hidden[] }>();

const { t } = useI18n();
const hiddenType = ref<"hour" | "day" | "month" | "year" | null>(null);

const groupedHidden = computed(() => {
  const result: Record<string, Hidden[]> = {};
  for (const owner of ownerOrder) {
    const items = props.hidden.filter((h) => h.owner === owner);
    if (items.length > 0) {
      result[owner] = items;
    }
  }
  return result;
});
</script>

<template>
  <div class="flex w-full divide-x divide-ternary rounded-xl bg-[#FDFDFD]">
    <div
      class="flex flex-col items-center py-2 gap-4 flex-1"
      @click="hiddenType = 'hour'"
    >
      <div
        class="size-11 rounded-xl bg-[#FAEFE7] border border-gold flex items-center justify-center font-semibold text-xl leading-[24px]"
      >
        {{ groupedHidden["hour"][0].stem }}
      </div>
      <span class="text-base leading-[20px]">
        {{ t("calculators.bazi.results.birthday-map.times.hour") }}
      </span>
    </div>

    <div
      class="flex flex-col items-center py-2 gap-4 flex-1"
      @click="hiddenType = 'day'"
    >
      <div
        class="size-11 rounded-xl bg-[#FAEFE7] border border-gold flex items-center justify-center font-semibold text-xl leading-[24px]"
      >
        {{ groupedHidden["day"][0].stem }}
      </div>
      <span class="text-base leading-[20px]">
        {{ t("calculators.bazi.results.birthday-map.times.day") }}
      </span>
    </div>

    <div
      class="flex flex-col items-center py-2 gap-4 flex-1"
      @click="hiddenType = 'month'"
    >
      <div
        class="size-11 rounded-xl bg-[#FAEFE7] border border-gold flex items-center justify-center font-semibold text-xl leading-[24px]"
      >
        {{ groupedHidden["month"][0].stem }}
      </div>
      <span class="text-base leading-[20px]">
        {{ t("calculators.bazi.results.birthday-map.times.month") }}
      </span>
    </div>

    <div
      class="flex flex-col items-center py-2 gap-4 flex-1"
      @click="hiddenType = 'year'"
    >
      <div
        class="size-11 rounded-xl bg-[#FAEFE7] border border-gold flex items-center justify-center font-semibold text-xl leading-[24px]"
      >
        {{ groupedHidden["year"][0].stem }}
      </div>
      <span class="text-base leading-[20px]">
        {{ t("calculators.bazi.results.birthday-map.times.year") }}
      </span>
    </div>
    <ModalComponent
      :show="hiddenType !== null"
      variant="fullscreen"
      @close="hiddenType = null"
      container-class="bg-beige"
    >
      <template #header>
        <p class="font-semibold text-2xl text-black">
          {{ t(`calculators.bazi.modal.hidden.title.${hiddenType}`) }}
        </p>
      </template>
      <template #content>
        <div
          class="flex flex-col gap-6 h-full overflow-y-auto bg-white rounded-lg p-4"
        >
          <!-- STEM -->
          <div
            v-for="({ stem, labels, tips }, idx) in groupedHidden[hiddenType]"
            :key="idx + hiddenType + 'stem'"
            class="flex flex-col gap-4"
          >
            <p class="font-semibold text-2xl text-black text-left">
              {{ stem }}
              {{ t("calculators.bazi.results.birthday-map.stem.heaven-stem") }}
            </p>
            <h5
              class="font-semibold text-xl leading-[24px] text-black text-left"
            >
              {{ labels.stem }}
            </h5>
            <p class="text-left text-base leading-[20px] text-brown">
              {{ tips.stem }}
            </p>
          </div>

          <!-- BRANCH -->
          <div class="flex flex-col gap-4">
            <p class="font-semibold text-2xl text-black text-left">
              {{ groupedHidden[hiddenType][0].branch }}
              {{ t("calculators.bazi.results.birthday-map.stem.earth-branch") }}
            </p>
            <h5
              class="font-semibold text-xl leading-[24px] text-black text-left"
            >
              {{ groupedHidden[hiddenType][0].labels.branch }}
            </h5>
            <p class="text-left text-base leading-[20px] text-brown">
              {{ groupedHidden[hiddenType][0].tips.stem }}
            </p>
          </div>
          <!-- STAGE -->
          <div
            v-for="({ stage, labels, tips }, idx) in groupedHidden[hiddenType]"
            :key="idx + hiddenType + 'stage'"
            class="flex flex-col gap-4"
          >
            <p class="font-semibold text-2xl text-black text-left">
              {{ stage }}
              {{ t("calculators.bazi.results.birthday-map.stem.stage") }}
            </p>
            <h5
              class="font-semibold text-xl leading-[24px] text-black text-left"
            >
              {{ labels.stage }}
            </h5>
            <p class="text-left text-base leading-[20px] text-brown">
              {{ tips.stage }}
            </p>
          </div>
          <!-- ELEMENT -->
          <div
            v-for="({ element, tips }, idx) in groupedHidden[hiddenType]"
            :key="idx + hiddenType + 'element'"
            class="flex flex-col gap-4"
          >
            <p class="font-semibold text-2xl text-black text-left">
              {{ t(`calculators.bazi.modal.hidden.element.${element}`) }}
            </p>
            <p class="text-left text-base leading-[20px] text-brown">
              {{ tips.element }}
            </p>
          </div>
          <!-- GOD -->
          <div
            v-for="({ god, labels, tips }, idx) in groupedHidden[hiddenType]"
            :key="idx + hiddenType + 'god'"
            class="flex flex-col gap-4"
          >
            <p class="font-semibold text-2xl text-black text-left">
              {{ god }}
              {{ t("calculators.bazi.results.birthday-map.stem.god") }}
            </p>
            <h5
              class="font-semibold text-xl leading-[24px] text-black text-left"
            >
              {{ labels.god }}
            </h5>
            <p class="text-left text-base leading-[20px] text-brown">
              {{ tips.god }}
            </p>
          </div>
        </div>
      </template>
    </ModalComponent>
  </div>
</template>
